// @sb-ai-platform/schemas
//
// TypeBox-backed request/response schemas for the public REST API
// (control-plane) + (later) tRPC router types for BFF↔SPA.
//
// **Boundary discipline** (per CLAUDE.md "Universal rules"):
//   - `schemas` imports from `platform-contracts`. The reverse is
//     forbidden and `pnpm lint:boundaries` enforces.
//   - `schemas` does NOT import from any implementation package
//     (`identity`, `provider`, `control-plane`, ...). HTTP-shape types
//     belong here; runtime behaviour lives in the impl packages.
//
// **fn-1.13 scope**: TypeBox schemas for the session + agent + webhook
// surface. Each schema gets a stable `$id` so `@fastify/swagger`
// emits the matching `components.schemas.<Id>` entry in the spec
// (and downstream SDK codegen consumes the same id).
//
// `Static<typeof X>` derives the TypeScript type from the schema so
// route handlers + the `sbp` CLI consume one source of truth.
import { Type } from '@sinclair/typebox';
import { MEMORY_ACCESS_MODES } from './agent-memory-access.js';
// ─────────────────────────────────────────────────────────────────
// Common scalar fragments
// ─────────────────────────────────────────────────────────────────
/**
 * Stable session-status union — mirrors `SessionStatus` in
 * `packages/migrations/src/schema/sessions.ts`. Hard-coded here
 * (not re-imported) because schemas owns HTTP types and must not
 * cross the impl boundary.
 */
export const SessionStatusSchema = Type.Union([
    Type.Literal('pending'),
    Type.Literal('running'),
    Type.Literal('awaiting_user'),
    Type.Literal('awaiting_approval'),
    Type.Literal('completed'),
    Type.Literal('failed'),
    Type.Literal('cancelled'),
    Type.Literal('expired'),
], { $id: 'SessionStatus' });
/** Stable session entry-point union. */
export const SessionEntryPointSchema = Type.Union([Type.Literal('user'), Type.Literal('cron'), Type.Literal('webhook')], { $id: 'SessionEntryPoint' });
/**
 * fn-79 — stable session-audience union. `'user'` = the session belongs in
 * the owning user's list views (conversation list, Agent Inbox); `'internal'`
 * = a Smokeball-internal session whose output is consumed programmatically —
 * hidden from user list views by default, but fully reachable by id
 * (visibility filtering only, NEVER access control).
 *
 * Orthogonal by construction to `entry_point` (who initiated), execution
 * mode, and `session_kind` — audience is NEVER derived from any of those
 * (R3): only the initiating caller knows it. This schema is the single
 * wire-side source of the enum; the migrations `SESSION_AUDIENCES` const
 * mirrors it DB-side (same relationship as `SessionStatus`).
 */
export const SessionAudienceSchema = Type.Union([Type.Literal('user'), Type.Literal('internal')], {
    $id: 'SessionAudience',
});
/**
 * Runtime guard for {@link SessionAudience}. Lives HERE (schemas owns the
 * wire enum) so non-TypeBox entry points (the cron tick's unvalidated
 * EventBridge payload) can validate against the SAME union without
 * duplicating the literal list (Universal Rule: route layers delegate to
 * `schemas`, never re-declare enums).
 */
export function isSessionAudience(value) {
    return value === 'user' || value === 'internal';
}
/**
 * fn-3 — stable session memory-scope union (`user` | `firm` | `matter`).
 * Used by the DETAIL projection's public `memory` field
 * ({@link SessionDetailSchema}). The REQUEST field stays a loose
 * `string[]` on {@link CreateSessionBodySchema} — a strict union there
 * would make Ajv reject unknown scopes with a generic 400 before the
 * handler runs; the exact `memory_config_invalid` code comes from the
 * shared normalizer in `./session-memory.ts`.
 */
export const SessionMemoryScopeSchema = Type.Union([Type.Literal('user'), Type.Literal('firm'), Type.Literal('matter')], { $id: 'SessionMemoryScope' });
/**
 * Stable error envelope. Every 4xx / 5xx response from the API
 * returns this shape so consumers (SDK, CLI, BFF) can pattern-match
 * without dispatching on status code alone.
 *
 * `check_code` is the tenancy-authz-style discriminator we need for
 * fn-1.13's 403 surface — `tenancy_user_id_mismatch`,
 * `tenancy_account_id_mismatch`, `tenancy_matter_not_owned`,
 * `tenancy_service_account_no_permission`. Other 4xx use the same
 * envelope with their own codes.
 */
export const ErrorEnvelopeSchema = Type.Object({
    error: Type.String({ description: 'Stable error code (e.g. "unauthorized").' }),
    message: Type.String({ description: 'Human-readable message; safe to surface.' }),
    check_code: Type.Optional(Type.String({
        description: 'Specific check that failed — for tenancy authz this is the `tenancy_*_mismatch` discriminator.',
    })),
    details: Type.Optional(Type.Unknown()),
}, { $id: 'ErrorEnvelope' });
export const ConfigListSortDirectionSchema = Type.Union([Type.Literal('asc'), Type.Literal('desc')], { $id: 'ConfigListSortDirection' });
// ─────────────────────────────────────────────────────────────────
// Session schemas
// ─────────────────────────────────────────────────────────────────
/**
 * Wire shape of a session. Snake_case at the HTTP boundary — every
 * field matches the column name in `sessions` (per spec § API
 * Contracts + the `agents` / `sessions` Drizzle schema).
 */
export const SessionSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    user_id: Type.String(),
    account_id: Type.String(),
    matter_id: Type.Union([Type.String(), Type.Null()]),
    agent_id: Type.String({ format: 'uuid' }),
    agent_version_id: Type.String({ format: 'uuid' }),
    provider_id: Type.String({ format: 'uuid' }),
    status: Type.Ref(SessionStatusSchema),
    provider_session_ref: Type.Union([Type.String(), Type.Null()]),
    entry_point: Type.Ref(SessionEntryPointSchema),
    // fn-79 (R4) — audience IS public wire state (unlike the internal
    // `session_kind`/`resolved_tool_scope` columns, which are deliberately
    // omitted here). Set once at create; immutable thereafter.
    audience: Type.Ref(SessionAudienceSchema),
    actor_identity: Type.Union([Type.String(), Type.Null()]),
    cost_used_tokens: Type.Integer({ minimum: 0 }),
    cost_used_tool_calls: Type.Integer({ minimum: 0 }),
    metadata: Type.Union([Type.Record(Type.String(), Type.Unknown()), Type.Null()]),
    created_at: Type.String({ format: 'date-time' }),
    last_updated_at: Type.String({ format: 'date-time' }),
    terminal_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    s3_offload_uri: Type.Union([Type.String(), Type.Null()]),
}, { $id: 'Session' });
/**
 * fn-3 (R4) — the session DETAIL projection: the shared {@link SessionSchema}
 * plus the optional public `memory` field (the attached memory scopes, in
 * canonical order `user`, `firm`, `matter`). Consumed by `GET /sessions/:id`
 * AND the `POST /sessions` 201 response (the creator shouldn't need a second
 * fetch); the LIST projection ({@link ListSessionsResponseSchema}) deliberately
 * stays on the plain `Session` shape and never carries `memory`.
 *
 * The field derives ONLY from a COMPLETE post-read-back `memory_config`
 * (every requested scope has a provider-read-back `mount_path`) — a
 * failed/partial session fetched by id OMITS the field rather than
 * masquerading as memory-attached. The internal `memory_config` column
 * itself is never on the public wire.
 */
export const SessionDetailSchema = Type.Composite([
    SessionSchema,
    Type.Object({
        memory: Type.Optional(Type.Array(Type.Ref(SessionMemoryScopeSchema), {
            description: 'Memory scopes attached to this session, in canonical order `user`, `firm`, ' +
                '`matter`. Present only when every requested scope was provisioned, attached, ' +
                'and its provider mount path read back and persisted — a failed/partial ' +
                'session omits the field.',
        })),
    }),
], { $id: 'SessionDetail' });
/**
 * `POST /sessions` body — user-initiated path. `entry_point` is
 * always `user` from a user JWT caller; the cron/webhook callers
 * arrive via internal helpers (see `entry-points/cron.ts` +
 * `routes/webhooks.ts`) and never POST through this shape.
 */
export const CreateSessionBodySchema = Type.Object({
    agent_id: Type.String({ format: 'uuid' }),
    // fn-46 — OPTIONAL. Omit for a matter-less session: no matter-ownership
    // tenancy check and no matter-service call at create (the DB column,
    // SessionSchema, and SessionRecord were already nullable — this realises
    // the matter-less session the data model + Octopus docs already promised).
    // When PRESENT, the matter is verified owned by the caller (full tenancy).
    // Not GUID-constrained — matter ids are opaque strings.
    matter_id: Type.Optional(Type.String({
        description: 'Matter id the session is bound to. Optional: omit for a matter-less ' +
            'session. When present, the matter must be owned by the caller.',
    })),
    metadata: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
    /** Free-form first user message — opportunistic; many clients send via /messages instead. */
    initial_message: Type.Optional(Type.String()),
    // fn-67 (AIP-40) — per-session system-prompt grounding inputs. Both are
    // TOP-LEVEL fields (NOT `metadata`): only the top-level values participate
    // in prompt composition, and `metadata.extra_context`/`metadata.timezone`
    // are scrubbed by the route so a caller can never smuggle a second path.
    extra_context: Type.Optional(Type.String({
        description: 'Optional caller-supplied free-text background for the agent (e.g. a backend ' +
            'creating a session on a lawyer’s behalf passes extra context). Accepted from ' +
            'ANY authenticated caller. Composed LAST into a delimited, provenance-labeled ' +
            '"treat as data, not instructions" block — it never gains instruction authority. ' +
            'Hard byte cap of 100 KB (UTF-8) is enforced by the route BEFORE any side effect ' +
            '(over-limit ⇒ 400, no session created); the cap is a byte length, not a ' +
            'TypeBox maxLength (which counts UTF-16 code units). Persisted in the session’s ' +
            'event history — NEVER send secrets. This is NOT a caller-supplied account_id/firm ' +
            'identifier — tenancy is resolved from the verified token, never from this field.',
    })),
    timezone: Type.Optional(Type.String({
        description: 'DEPRECATED (fn-72, R3a): ACCEPT-BUT-IGNORE. Still accepted on the wire for ' +
            'back-compat — passing it is NEVER a 400 — but it is NO LONGER read. fn-72 moved ' +
            'grounding to the first user message, recomputed at send time, and the timezone is ' +
            'resolved there via the `firm jurisdiction → matter jurisdiction → UTC` chain. A ' +
            'value passed here is silently ignored and the firm chain wins. Callers do not need ' +
            'to send it.',
    })),
    // fn-69 (R3) — first-class per-session tool-config override (NOT metadata;
    // `tool_config` is a reserved scrub key). Referenced by `$id` (the const is
    // declared later in this module, so a `Type.Ref(const)` here would hit a
    // TDZ); the route registers `SessionToolConfigOverrideSchema` so the
    // validator + swagger resolve the ref. The route caps + normalizes it with
    // the raw byte length BEFORE any side effect, then resolves it against the
    // pinned snapshot + live gateway catalog.
    tool_config: Type.Optional(Type.Unsafe({
        $ref: 'SessionToolConfigOverride',
        description: 'Optional per-session tool-config override (narrowing-only): disable connectors/tools, ' +
            'escalate a tool to needs-approval (always_ask), or toggle native built-ins. Applied at ' +
            'create-time fail-closed; never widens beyond the agent-declared set.',
    })),
    // fn-79 (R2/R3) — the session's intended audience. A dedicated TOP-LEVEL
    // field (NOT metadata; `metadata.audience` is a reserved scrub key).
    // Strict literals: empty string / case variants / unknown values are a
    // 400 with NO session created — no trim/case-fold normalization.
    audience: Type.Optional(Type.Ref(SessionAudienceSchema, {
        description: "Intended audience of the session. `'user'` (the default when omitted) shows the " +
            "session in the owning user's list views; `'internal'` hides it from user list views " +
            '(opt back in via `GET /sessions?include_internal=true`) while keeping every by-id ' +
            'endpoint fully functional — visibility filtering, not access control. Settable by ' +
            'ANY authenticated caller (user or service principal); NEVER derived from entry ' +
            'point, actor type, or execution mode. Immutable after create — no update path ' +
            'accepts it.',
    })),
    // fn-3 (R1/R3) — per-session memory-store attachments. A dedicated
    // TOP-LEVEL field (NOT metadata; `memory` + `memory_config` are reserved
    // scrub keys). DELIBERATELY loose (`string[]`, not a literal union): a
    // strict union would make Ajv reject an unknown scope with a generic 400
    // before the handler runs — the scope enum + duplicate rejection live in
    // the shared `normalizeSessionMemoryScopes` normalizer
    // (`./session-memory.ts`), which the route maps to the exact
    // `400 memory_config_invalid` code.
    memory: Type.Optional(Type.Array(Type.String(), {
        description: 'Memory scopes to attach to the session: any of `user`, `firm`, `matter` ' +
            '(exact lowercase literals, no duplicates; `[]` is a no-op). Access defaults ' +
            'to platform policy — `user`/`firm` mount read-only, `matter` mounts ' +
            "read-write — unless the agent's config declares a per-scope override (the " +
            'config-repo `memory:` access map, echoed on `GET /agents`; `firm` always ' +
            'stays read-only). Never caller-settable — there is no per-session access ' +
            'input. `matter` requires `matter_id`; `user` requires a user (not ' +
            'service) principal. Requires session memory to be enabled on the deployment ' +
            'and supported by the provider, and the agent to carry the built-in agent ' +
            'toolset — else `400 memory_not_enabled` / `400 memory_config_invalid`. ' +
            'Attachment is immutable for the session’s life.',
    })),
}, { $id: 'CreateSessionBody', additionalProperties: false });
/** `GET /sessions` query — pagination + filter. */
export const ListSessionsQuerySchema = Type.Object({
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200 })),
    cursor: Type.Optional(Type.String()),
    status: Type.Optional(Type.Ref(SessionStatusSchema)),
    matter_id: Type.Optional(Type.String()),
    /**
     * fn-2.x — when `true`, archived sessions are included. Defaults to
     * `false` (archived rows hidden) so operators see a clean list.
     */
    include_archived: Type.Optional(Type.Boolean()),
    /**
     * fn-79 (R4) — when `true`, `audience='internal'` sessions are included.
     * Defaults to `false` (internal rows hidden) so user list views stay
     * clean. Mirrors `include_archived` (Ajv coerces only the literal
     * `"true"`/`"false"` query strings).
     */
    include_internal: Type.Optional(Type.Boolean({
        description: "Include `audience='internal'` sessions in the list (hidden by default). " +
            'Pagination cursors are scoped to the filter set they were minted under — ' +
            'toggling this (or any filter) mid-pagination is unsupported; restart ' +
            'pagination when changing filters.',
    })),
}, { $id: 'ListSessionsQuery', additionalProperties: false });
/** `GET /sessions` response — paginated. */
export const ListSessionsResponseSchema = Type.Object({
    data: Type.Array(Type.Ref(SessionSchema)),
    next_cursor: Type.Union([Type.String(), Type.Null()]),
}, { $id: 'ListSessionsResponse' });
/**
 * `GET /sessions/:id/events` query. `after_id` is opaque per the
 * provider boundary (spec § Streaming — "treat event IDs as opaque").
 *
 * **fn-49.1** — `order` selects the sort direction of the page.
 * Optional; defaults to ascending (chronological — unchanged
 * behavior when absent). `order=desc` together with `after_id` is
 * rejected (desc cursor pagination semantics are undefined) — the
 * cross-field rule lives in {@link normalizeGetSessionEventsQuery},
 * which BOTH the control plane route and the BFF input parser call.
 */
export const GetSessionEventsQuerySchema = Type.Object({
    after_id: Type.Optional(Type.String({ minLength: 1 })),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 1000 })),
    order: Type.Optional(Type.Union([Type.Literal('asc'), Type.Literal('desc')])),
}, { $id: 'GetSessionEventsQuery', additionalProperties: false });
/**
 * Typed validation error thrown by {@link normalizeGetSessionEventsQuery}.
 * Framework-neutral — the control plane maps it to a Fastify `400`
 * ErrorEnvelope; the BFF lets tRPC wrap it into `BAD_REQUEST`.
 */
export class GetSessionEventsQueryValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'GetSessionEventsQueryValidationError';
    }
}
/**
 * Shared normalizer for the `GET /sessions/:id/events` query shape
 * (CLAUDE.md rule: request normalizers for HTTP/tRPC inputs live in
 * `schemas`; consumers never duplicate enum lists or cross-field
 * checks). Validates:
 *
 *   - `after_id`: non-empty string when present,
 *   - `limit`: integer in [1, 1000] when present,
 *   - `order`: `'asc' | 'desc'` when present,
 *   - cross-field: `order === 'desc'` together with `after_id` is
 *     rejected — desc pagination semantics are undefined (and the
 *     `after_id` shim is dead upstream; a defined 400 beats silent
 *     wrong pagination).
 *
 * Throws {@link GetSessionEventsQueryValidationError} on any
 * violation; returns the normalized (typed, snake_case) query
 * otherwise. Both the control plane route handler and the BFF
 * `parseSessionEventsInput` delegate here and map the error to
 * their framework surface (Fastify 400 / tRPC BAD_REQUEST).
 */
export function normalizeGetSessionEventsQuery(q) {
    if (q === null || q === undefined)
        return {};
    if (typeof q !== 'object') {
        throw new GetSessionEventsQueryValidationError('events query must be an object');
    }
    const out = {};
    if (q.after_id !== undefined) {
        if (typeof q.after_id !== 'string' || q.after_id.length === 0) {
            throw new GetSessionEventsQueryValidationError('after_id must be a non-empty string');
        }
        out.after_id = q.after_id;
    }
    if (q.limit !== undefined) {
        if (typeof q.limit !== 'number' ||
            !Number.isFinite(q.limit) ||
            !Number.isInteger(q.limit) ||
            q.limit < 1 ||
            q.limit > 1000) {
            throw new GetSessionEventsQueryValidationError('limit must be an integer in [1, 1000]');
        }
        out.limit = q.limit;
    }
    if (q.order !== undefined) {
        if (q.order !== 'asc' && q.order !== 'desc') {
            throw new GetSessionEventsQueryValidationError("order must be 'asc' or 'desc'");
        }
        out.order = q.order;
    }
    // Lockstep with GetSessionEventsQuerySchema's additionalProperties:
    // false — unknown keys are a contract violation here too, not
    // silently dropped (the schema and the normalizer must accept the
    // same set of requests).
    for (const key of Object.keys(q)) {
        if (key !== 'after_id' && key !== 'limit' && key !== 'order') {
            throw new GetSessionEventsQueryValidationError(`unknown query key: ${key}`);
        }
    }
    if (out.order === 'desc' && out.after_id !== undefined) {
        throw new GetSessionEventsQueryValidationError('order=desc cannot be combined with after_id (descending cursor pagination is undefined)');
    }
    return out;
}
/**
 * A single event envelope returned from `GET /sessions/:id/events`.
 * `data` is the verbatim MA event payload; the control plane only
 * wraps + adds Smokeball metadata (e.g. `decision_actor_user_id`)
 * for `requires_action` events.
 */
export const SessionEventEnvelopeSchema = Type.Object({
    id: Type.String(),
    type: Type.String({ description: 'MA event type (e.g. "agent.message").' }),
    data: Type.Record(Type.String(), Type.Unknown()),
    smokeball: Type.Optional(Type.Object({
        decision_actor_user_id: Type.Optional(Type.Union([Type.String(), Type.Null()])),
        approval_id: Type.Optional(Type.String({ format: 'uuid' })),
        decision: Type.Optional(Type.Union([Type.Literal('approved'), Type.Literal('rejected'), Type.Null()])),
    }, { additionalProperties: false })),
}, { $id: 'SessionEventEnvelope' });
export const GetSessionEventsResponseSchema = Type.Object({
    data: Type.Array(Type.Ref(SessionEventEnvelopeSchema)),
    next_cursor: Type.Union([Type.String(), Type.Null()]),
}, { $id: 'GetSessionEventsResponse' });
/** `POST /sessions/:id/messages` body. */
export const PostSessionMessageBodySchema = Type.Object({
    content: Type.String({ minLength: 1 }),
}, { $id: 'PostSessionMessageBody', additionalProperties: false });
/** `POST /sessions/:id/messages` response. */
export const PostSessionMessageResponseSchema = Type.Object({
    accepted: Type.Boolean(),
    session_id: Type.String({ format: 'uuid' }),
    status: Type.Ref(SessionStatusSchema),
}, { $id: 'PostSessionMessageResponse' });
// ─────────────────────────────────────────────────────────────────
// Agent schemas
// ─────────────────────────────────────────────────────────────────
// fn-8 (AIP-28) — agents are global-only. The `account_id` / `visibility`
// tenancy axis (ADR 0013) was never wired to the product and is collapsed
// out; the git config repo (scan/deploy) is the sole agent-mutation surface.
// ADR 0016 supersedes ADR 0013.
// fn-71 / ADR 0035 — provenance discriminant on the agent response. `source`
// is FIRST-CLASS so a console caller can tell a config-repo-authored agent
// (edited via the git repo only) from a console-authored one (editable via
// `PATCH /agents/:id`). The canonical value list lives in `@sb-ai-platform/
// migrations`; redeclared here as a `Type.Union` literal so the HTTP contract
// carries it without a runtime dep on the migrations package.
export const AgentSourceSchema = Type.Union([Type.Literal('config_repo'), Type.Literal('console')], { $id: 'AgentSource' });
// fn-80 / ADR 0039 — one memory access mode on the wire (the `Agent.memory`
// read echo below). DERIVED from the shared `MEMORY_ACCESS_MODES` const
// (`./agent-memory-access.ts` — the single runtime definition both fail-closed
// layers already import), so the wire literal union can never drift from the
// parser. No `$id`: inlined where used (the `user`/`matter` slots; `firm` is
// narrower — `read_only` only, since `firm: read_write` is deploy-rejected by
// the shared parser and can never appear on a pinned snapshot).
const MemoryAccessModeWireSchema = Type.Union(MEMORY_ACCESS_MODES.map((m) => Type.Literal(m)));
export const AgentSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    name: Type.String(),
    prompt: Type.String(),
    provider_id: Type.String({ format: 'uuid' }),
    current_version_id: Type.String({ format: 'uuid' }),
    max_tokens: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    max_tool_calls: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    config: Type.Union([Type.Record(Type.String(), Type.Unknown()), Type.Null()]),
    // fn-77 follow-up — the AUTHORING ECHO. `POST`/`PATCH /agents` accept
    // top-level `description`/`model`/`skills`/`network_profile`, and PATCH is
    // an honest FULL-REPLACE, so the read projection must echo them back or a
    // caller cannot fetch-modify-resend. All four are projected from the
    // persisted `config_snapshot` (the provisioning engine pins them there);
    // they are echoed for BOTH sources when the snapshot carries them.
    //
    // `description` is always present (`null` when the snapshot predates the
    // echo or the author omitted it). The other three are OPTIONAL — omitted
    // when not derivable from the snapshot (e.g. a legacy/pre-fn-68 config-repo
    // snapshot with no pinned `network_profile`) — never fabricated.
    description: Type.Union([Type.String(), Type.Null()]),
    /** The PINNED model (`config_snapshot.model`, normalized `{ id, speed? }`). */
    model: Type.Optional(Type.Object({ id: Type.String(), speed: Type.Optional(Type.String()) }, { additionalProperties: false })),
    /** Authored skill names (`config_snapshot.dependency_summary.skills`). */
    skills: Type.Optional(Type.Array(Type.String())),
    /** The RESOLVED network-profile name (`config_snapshot.network_profile.name`). */
    network_profile: Type.Optional(Type.String()),
    // fn-80 / ADR 0039 — the PINNED agent-config `memory:` access map
    // (`config_snapshot.memory`): per-scope memory access-mode OVERRIDES the
    // agent's config-repo YAML declared (`firm: read_write` is deploy-rejected,
    // so it can never appear here). READ ECHO ONLY — authorable exclusively via
    // the reviewed config repo; the `POST`/`PATCH /agents` bodies stay closed
    // (`additionalProperties: false` — the fn-80 R7 rejection), so unlike the
    // four fn-77 echo fields above this one is NOT fetch-modify-resendable.
    // Omitted when the agent declares no overrides (part-1 policy defaults
    // apply: `user`/`firm` read-only, `matter` read-write).
    memory: Type.Optional(Type.Object({
        user: Type.Optional(MemoryAccessModeWireSchema),
        // `read_only` ONLY — `firm: read_write` is deploy-rejected (fn-80
        // R4), so the read contract is exactly as narrow as reality.
        firm: Type.Optional(Type.Literal('read_only')),
        matter: Type.Optional(MemoryAccessModeWireSchema),
    }, {
        additionalProperties: false,
        description: "The agent's per-scope memory access-mode overrides (config-repo " +
            '`memory:` access map, pinned at deploy). A listed scope overrides the ' +
            'platform default for sessions of this agent; an unlisted scope keeps ' +
            'the default (`user`/`firm` read-only, `matter` read-write). ' +
            '`firm: read_write` is rejected at deploy and never appears. ' +
            'Read-only echo — not settable via `POST`/`PATCH /agents`.',
    })),
    // fn-71 / ADR 0035 — provenance. `source` is the durable discriminant the
    // console branches editing on.
    source: Type.Ref(AgentSourceSchema),
    // fn-75 / ADR 0035 — DERIVED soft-archive flag (true iff the row's
    // `status='archived'`). The raw `status` enum is NEVER on the wire — reads
    // surface `active` OR `archived` rows (an admin's include-archived view),
    // so consumers (SPA / BFF / SDK) branch on this single boolean, not status.
    // `provisioning`/`failed` rows are never emitted, so `archived:false` here
    // always means the agent is `active`.
    archived: Type.Boolean(),
    created_at: Type.String({ format: 'date-time' }),
    updated_at: Type.String({ format: 'date-time' }),
}, { $id: 'Agent' });
export const AgentVersionSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    agent_id: Type.String({ format: 'uuid' }),
    version_number: Type.Integer({ minimum: 1 }),
    prompt_snapshot: Type.String(),
    config_snapshot: Type.Union([Type.Record(Type.String(), Type.Unknown()), Type.Null()]),
    published_by: Type.String(),
    published_at: Type.String({ format: 'date-time' }),
}, { $id: 'AgentVersion' });
/** Agent + its current version, returned from `GET /agents/:id`. */
export const AgentWithVersionSchema = Type.Object({
    agent: Type.Ref(AgentSchema),
    current_version: Type.Ref(AgentVersionSchema),
}, { $id: 'AgentWithVersion' });
// fn-8 (AIP-28) — no `visibility` filter; agents are global-only.
export const ListAgentsQuerySchema = Type.Object({
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200 })),
    cursor: Type.Optional(Type.String()),
    /**
     * fn-75 / ADR 0035 — when `true`, the list widens to include soft-archived
     * (`status='archived'`) console agents alongside the active ones; the
     * default (`false`/undefined) stays active-only so archived agents are
     * hidden from the public list + the start-chat picker until an admin opts
     * in. `provisioning`/`failed` rows are never listed either way.
     */
    include_archived: Type.Optional(Type.Boolean()),
}, { $id: 'ListAgentsQuery', additionalProperties: false });
export const ListAgentsResponseSchema = Type.Object({
    data: Type.Array(Type.Ref(AgentSchema)),
    next_cursor: Type.Union([Type.String(), Type.Null()]),
}, { $id: 'ListAgentsResponse' });
// fn-71 / ADR 0035 — `POST /agents` + `PATCH /agents/:id` are RE-INTRODUCED as
// the DB-backed console write path (`source='console'`). fn-8 (AIP-28) removed
// them; this reverses ADR 0016 D9 ONLY (agents stay global — D8 preserved, NO
// `account_id`/`tenant`/`firm` field anywhere). The request bodies +
// {@link normalizeAgentModel} live below, after {@link AGENT_NAME_SLUG}.
// ─────────────────────────────────────────────────────────────────
// Config-repo agent schemas
// ─────────────────────────────────────────────────────────────────
export const AGENT_NAME_SLUG = /^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$/;
export function normalizeAgentModel(model) {
    if (typeof model === 'string') {
        if (model.trim().length === 0) {
            return { ok: false, error: 'missing required field: model' };
        }
        return { ok: true, value: { id: model.trim() } };
    }
    if (model === undefined || model === null) {
        return { ok: false, error: 'missing required field: model' };
    }
    if (typeof model !== 'object' || Array.isArray(model)) {
        return { ok: false, error: 'field model must be a string or mapping' };
    }
    const m = model;
    if (typeof m.id !== 'string' || m.id.trim().length === 0) {
        return { ok: false, error: 'missing required field: model.id' };
    }
    if (m.speed !== undefined && typeof m.speed !== 'string') {
        return { ok: false, error: 'field model.speed must be a string when present' };
    }
    return {
        ok: true,
        value: {
            id: m.id.trim(),
            ...(typeof m.speed === 'string' ? { speed: m.speed } : {}),
        },
    };
}
/**
 * The agent-model field as it arrives on the wire — a bare model-id string OR a
 * `{ id, speed? }` mapping (mirrors the config-repo YAML author form). The route
 * normalizes it via {@link normalizeAgentModel}.
 */
export const AgentModelInputSchema = Type.Union([
    Type.String({ minLength: 1 }),
    Type.Object({ id: Type.String({ minLength: 1 }), speed: Type.Optional(Type.String()) }, { additionalProperties: false }),
], { $id: 'AgentModelInput' });
// The authoring fields common to CREATE + UPDATE. NO tenancy fields
// (AIP-28 R1 / ADR 0016 D8 preserved): no `account_id`/`tenant`/`firm`. The
// author-verbatim MA `tools[]` (the fn-61 authz-bearing shape) are opaque
// records here; the control-plane resolver validates + qualifies them, and
// `skills[]` reference EXISTING config-repo skills by name (the form authors
// no new skill/MCP-server definitions).
const AgentAuthoringFields = {
    model: Type.Ref(AgentModelInputSchema),
    system: Type.String({ minLength: 1 }),
    description: Type.Optional(Type.String()),
    tools: Type.Optional(Type.Array(Type.Record(Type.String(), Type.Unknown()))),
    skills: Type.Optional(Type.Array(Type.String())),
    network_profile: Type.Optional(Type.String()),
};
/** `POST /agents` body (CREATE). `name` is an `AGENT_NAME_SLUG`. */
export const CreateAgentBodySchema = Type.Object({ name: Type.String({ pattern: AGENT_NAME_SLUG.source }), ...AgentAuthoringFields }, { $id: 'CreateAgentBody', additionalProperties: false });
/**
 * `PATCH /agents/:id` body (UPDATE). Full-replacement of the editable fields
 * (mirrors the config-repo deploy). `name` is IMMUTABLE — a rename is a
 * `UNIQUE(name)` collision risk → author a new agent — so it is NOT a PATCH
 * field; the manager reuses the existing name.
 */
export const UpdateAgentBodySchema = Type.Object({ ...AgentAuthoringFields }, { $id: 'UpdateAgentBody', additionalProperties: false });
/**
 * `POST /agents/configurable-tools/preview` body (fn-71 R10 — the DRAFT
 * tool-config data path). Builds tool-config panel rows for a NOT-yet-created
 * agent from the SELECTED MCP servers + the live gateway catalog (the
 * `GET /agents/:id/configurable-tools` baseline needs a pinned snapshot a new
 * agent lacks). The response is the same {@link ConfigurableToolsResponse} the
 * baseline endpoint returns (agent variant — no applied/`degraded` state).
 */
export const DraftConfigurableToolsBodySchema = Type.Object({
    /**
     * Selected MCP server names (the `mcp_server_name`s the author picked).
     * OPTIONAL with a `[]` default (fn-72) so a `{ whole_gateway: true }` body
     * validates on its own; IGNORED when `whole_gateway` is `true`.
     */
    mcp_servers: Type.Optional(Type.Array(Type.String(), { default: [] })),
    /**
     * fn-72 (R3) — when `true`, the CP IGNORES `mcp_servers` and discovers ALL
     * federated targets from the live gateway catalog, surfacing them under the
     * ONE logical `smokeball` connector (each tool tagged `target_id`+`area_label`).
     * Absent/`false` keeps the existing per-`mcp_servers` whole-server behaviour
     * (backward-compatible).
     */
    whole_gateway: Type.Optional(Type.Boolean()),
    /**
     * fn-74 (R3) — DIRECT-introspection opt-in: the `mcp_server_name`s the author
     * asked the CP to introspect per-tool by reading the connector's OWN
     * `tools/list` with the firm InfoTrack token (NOT via the AgentCore gateway).
     * The backend introspects ONLY names that are registered + `selectable` +
     * non-gateway + `supports_direct_introspection`; any other name is ignored.
     * OPTIONAL + backward-compatible — absent means "no direct introspection" and
     * a selected direct connector stays the fn-73 whole-server fallback. A direct
     * connector that introspects is projected per-tool (native `__` names,
     * `always_allow_locked:true`); a failed/login-required one degrades to the
     * connector-level fallback. NEVER blocks the gateway projection.
     */
    direct_mcp_servers: Type.Optional(Type.Array(Type.String())),
}, { $id: 'DraftConfigurableToolsBody', additionalProperties: false });
/**
 * Typed validation error thrown by the agent write-path input normalizers
 * below. Framework-neutral — the BFF lets tRPC wrap it into `BAD_REQUEST`; a CP
 * caller would map it to a Fastify `400`. (Mirrors
 * {@link GetSessionEventsQueryValidationError}.)
 */
export class AgentInputValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'AgentInputValidationError';
    }
}
/**
 * The editable authoring field names common to CREATE + UPDATE, DERIVED from the
 * canonical {@link AgentAuthoringFields} schema so a future schema-only field is
 * forwarded automatically. The BFF must NOT re-declare this list (CLAUDE.md:
 * `schemas` owns the HTTP/tRPC request contract; the reverse import is forbidden)
 * — a hand-maintained copy would silently strip any field added here.
 */
export const AGENT_AUTHORING_FIELD_NAMES = Object.keys(AgentAuthoringFields);
function pickAgentAuthoringFields(r) {
    const out = {};
    for (const key of AGENT_AUTHORING_FIELD_NAMES) {
        if (r[key] !== undefined)
            out[key] = r[key];
    }
    return out;
}
/**
 * Normalize the `agents.create` / `POST /agents` input → the `CreateAgentBody`
 * shape. Structural gate only (a non-empty `name`); forwards EVERY authoring
 * field present (derived from the schema, never a hand-listed subset). The
 * control plane re-validates authoritatively (strict TypeBox
 * `additionalProperties:false` + platform-admin authz). Throws
 * {@link AgentInputValidationError} on a bad shape.
 */
export function normalizeCreateAgentInput(raw) {
    if (raw === null || raw === undefined || typeof raw !== 'object') {
        throw new AgentInputValidationError('input must be an object with at least { name }');
    }
    const r = raw;
    const name = typeof r.name === 'string' ? r.name.trim() : '';
    if (name.length === 0) {
        throw new AgentInputValidationError('name must be a non-empty string');
    }
    return { name, ...pickAgentAuthoringFields(r) };
}
/**
 * Normalize the `agents.update` / `PATCH /agents/:id` input → a FLAT
 * `{ agentId, ...authoring-fields }` (symmetric with
 * {@link normalizeCreateAgentInput}; no `name` — rename is unsupported).
 * `agentId` is the path param. Throws {@link AgentInputValidationError}.
 */
export function normalizeUpdateAgentInput(raw) {
    if (raw === null || raw === undefined || typeof raw !== 'object') {
        throw new AgentInputValidationError('input must be an object with { agentId }');
    }
    const r = raw;
    const agentId = typeof r.agentId === 'string' ? r.agentId.trim() : '';
    if (agentId.length === 0) {
        throw new AgentInputValidationError('agentId must be a non-empty string');
    }
    return { agentId, ...pickAgentAuthoringFields(r) };
}
/**
 * Normalize the `agents.draftConfigurableTools` /
 * `POST /agents/configurable-tools/preview` input → the
 * `DraftConfigurableToolsBody`. `mcp_servers` defaults to `[]` (the panel may
 * render native built-ins before any server is picked); `whole_gateway` (fn-72)
 * is optional and, when `true`, makes the CP discover ALL federated targets and
 * IGNORE `mcp_servers`. Throws {@link AgentInputValidationError}.
 */
export function normalizeDraftConfigurableToolsInput(raw) {
    if (raw === null || raw === undefined || typeof raw !== 'object') {
        throw new AgentInputValidationError('input must be an object with { mcp_servers?, whole_gateway?, direct_mcp_servers? }');
    }
    const r = raw;
    if (r.whole_gateway !== undefined && typeof r.whole_gateway !== 'boolean') {
        throw new AgentInputValidationError('whole_gateway must be a boolean when present');
    }
    const wholeGateway = r.whole_gateway === true ? { whole_gateway: true } : {};
    // fn-74 (R3) — `direct_mcp_servers` is OPTIONAL; when present it must be an
    // array of strings. Omitted ⇒ no direct introspection (backward-compatible).
    let directServers = {};
    if (r.direct_mcp_servers !== undefined) {
        if (!Array.isArray(r.direct_mcp_servers) ||
            r.direct_mcp_servers.some((s) => typeof s !== 'string')) {
            throw new AgentInputValidationError('direct_mcp_servers must be an array of strings');
        }
        directServers = { direct_mcp_servers: r.direct_mcp_servers };
    }
    if (r.mcp_servers === undefined)
        return { mcp_servers: [], ...wholeGateway, ...directServers };
    if (!Array.isArray(r.mcp_servers) || r.mcp_servers.some((s) => typeof s !== 'string')) {
        throw new AgentInputValidationError('mcp_servers must be an array of strings');
    }
    return { mcp_servers: r.mcp_servers, ...wholeGateway, ...directServers };
}
export const ConfigRepoAgentStatusSchema = Type.Union([
    Type.Literal('new'),
    Type.Literal('in_sync'),
    Type.Literal('changed'),
    Type.Literal('unpublished'),
    Type.Literal('deleted'),
    Type.Literal('error'),
], { $id: 'ConfigRepoAgentStatus' });
export const ConfigRepoAgentSortBySchema = Type.Union([
    Type.Literal('name'),
    Type.Literal('status'),
    Type.Literal('updated_at'),
    Type.Literal('scanned_at'),
    Type.Literal('deployed_at'),
    Type.Literal('model_id'),
], { $id: 'ConfigRepoAgentSortBy' });
export const ListConfigRepoAgentsQuerySchema = Type.Object({
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200 })),
    cursor: Type.Optional(Type.String({ minLength: 1 })),
    q: Type.Optional(Type.String()),
    sort_by: Type.Optional(Type.Ref(ConfigRepoAgentSortBySchema)),
    sort_dir: Type.Optional(Type.Ref(ConfigListSortDirectionSchema)),
    status: Type.Optional(Type.Ref(ConfigRepoAgentStatusSchema)),
    model_id: Type.Optional(Type.String()),
    agent_ma_id: Type.Optional(Type.String()),
    skill_name: Type.Optional(Type.String()),
    mcp_server_name: Type.Optional(Type.String()),
}, { $id: 'ListConfigRepoAgentsQuery', additionalProperties: false });
export const ConfigRepoAgentSkillStatusSchema = Type.Union([Type.Literal('ok'), Type.Literal('missing'), Type.Literal('unpublished')], { $id: 'ConfigRepoAgentSkillStatus' });
export const ConfigRepoAgentSkillSchema = Type.Object({
    skill_name: Type.String(),
    ma_skill_id: Type.Union([Type.String(), Type.Null()]),
    status: Type.Ref(ConfigRepoAgentSkillStatusSchema),
}, { $id: 'ConfigRepoAgentSkill', additionalProperties: false });
export const ConfigRepoAgentMcpServerStatusSchema = Type.Union([Type.Literal('ok'), Type.Literal('missing'), Type.Literal('invalid')], { $id: 'ConfigRepoAgentMcpServerStatus' });
export const ConfigRepoAgentMcpServerSchema = Type.Object({
    name: Type.String(),
    url: Type.Union([Type.String(), Type.Null()]),
    status: Type.Ref(ConfigRepoAgentMcpServerStatusSchema),
}, { $id: 'ConfigRepoAgentMcpServer', additionalProperties: false });
export const ConfigRepoAgentSchema = Type.Object({
    // Platform agent DB id (UUID). Present only once the agent is deployed
    // (a DB `agents` row exists); `null` for repo-only / never-deployed
    // agents. fn-8 (AIP-28) C-SPA — the console's "Start chat" needs this to
    // create a session via `POST /sessions { agent_id, matter_id }`.
    agent_id: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    name: Type.String(),
    description: Type.Union([Type.String(), Type.Null()]),
    model_id: Type.Union([Type.String(), Type.Null()]),
    repo_blob_hash: Type.Union([Type.String(), Type.Null()]),
    deployed_blob_hash: Type.Union([Type.String(), Type.Null()]),
    ma_agent_id: Type.Union([Type.String(), Type.Null()]),
    ma_agent_version: Type.Union([Type.Number(), Type.Null()]),
    skills: Type.Array(Type.Ref(ConfigRepoAgentSkillSchema)),
    mcp_servers: Type.Array(Type.Ref(ConfigRepoAgentMcpServerSchema)),
    status: Type.Ref(ConfigRepoAgentStatusSchema),
    repo_path: Type.Union([Type.String(), Type.Null()]),
    scanned_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    deployed_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    updated_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    scan_error: Type.Union([Type.String(), Type.Null()]),
}, { $id: 'ConfigRepoAgent', additionalProperties: false });
export const ListConfigRepoAgentsResponseSchema = Type.Object({
    data: Type.Array(Type.Ref(ConfigRepoAgentSchema)),
    next_cursor: Type.Union([Type.String(), Type.Null()]),
    last_scan_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
}, { $id: 'ListConfigRepoAgentsResponse', additionalProperties: false });
export const ScanConfigRepoAgentsResponseSchema = Type.Object({
    scanned_at: Type.String({ format: 'date-time' }),
    discovered_count: Type.Integer({ minimum: 0 }),
    valid_count: Type.Integer({ minimum: 0 }),
    invalid_count: Type.Integer({ minimum: 0 }),
}, { $id: 'ScanConfigRepoAgentsResponse', additionalProperties: false });
export const DeployConfigRepoAgentResponseSchema = Type.Object({
    agent: Type.Ref(ConfigRepoAgentSchema),
}, { $id: 'DeployConfigRepoAgentResponse', additionalProperties: false });
// ─────────────────────────────────────────────────────────────────
// Config-repo skill schemas
// ─────────────────────────────────────────────────────────────────
export const SKILL_NAME_SLUG = /^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$/;
export const ConfigRepoSkillStatusSchema = Type.Union([
    Type.Literal('new'),
    Type.Literal('in_sync'),
    Type.Literal('changed'),
    Type.Literal('unpublished'),
    Type.Literal('deleted'),
    Type.Literal('error'),
], { $id: 'ConfigRepoSkillStatus' });
export const ConfigRepoSkillSortBySchema = Type.Union([
    Type.Literal('skill_name'),
    Type.Literal('status'),
    Type.Literal('updated_at'),
    Type.Literal('scanned_at'),
    Type.Literal('published_at'),
], { $id: 'ConfigRepoSkillSortBy' });
export const ListConfigRepoSkillsQuerySchema = Type.Object({
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200 })),
    cursor: Type.Optional(Type.String({ minLength: 1 })),
    q: Type.Optional(Type.String()),
    sort_by: Type.Optional(Type.Ref(ConfigRepoSkillSortBySchema)),
    sort_dir: Type.Optional(Type.Ref(ConfigListSortDirectionSchema)),
    status: Type.Optional(Type.Ref(ConfigRepoSkillStatusSchema)),
    skill_ma_id: Type.Optional(Type.String()),
}, { $id: 'ListConfigRepoSkillsQuery', additionalProperties: false });
export const ConfigRepoSkillSchema = Type.Object({
    skill_name: Type.String(),
    description: Type.Union([Type.String(), Type.Null()]),
    repo_tree_hash: Type.Union([Type.String(), Type.Null()]),
    published_tree_hash: Type.Union([Type.String(), Type.Null()]),
    ma_skill_id: Type.Union([Type.String(), Type.Null()]),
    status: Type.Ref(ConfigRepoSkillStatusSchema),
    repo_path: Type.Union([Type.String(), Type.Null()]),
    scanned_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    published_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    updated_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    scan_error: Type.Union([Type.String(), Type.Null()]),
}, { $id: 'ConfigRepoSkill', additionalProperties: false });
export const ListConfigRepoSkillsResponseSchema = Type.Object({
    data: Type.Array(Type.Ref(ConfigRepoSkillSchema)),
    next_cursor: Type.Union([Type.String(), Type.Null()]),
    last_scan_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
}, { $id: 'ListConfigRepoSkillsResponse', additionalProperties: false });
export const ScanConfigRepoResponseSchema = Type.Object({
    scanned_at: Type.String({ format: 'date-time' }),
    discovered_count: Type.Integer({ minimum: 0 }),
    valid_count: Type.Integer({ minimum: 0 }),
    invalid_count: Type.Integer({ minimum: 0 }),
}, { $id: 'ScanConfigRepoResponse', additionalProperties: false });
export const SyncConfigRepoSkillResponseSchema = ConfigRepoSkillSchema;
export const DeleteConfigRepoSkillResponseSchema = Type.Object({
    skill_name: Type.String({ pattern: SKILL_NAME_SLUG.source }),
    deleted: Type.Literal(true),
}, { $id: 'DeleteConfigRepoSkillResponse', additionalProperties: false });
export const SkillNameParamSchema = Type.Object({
    skill_name: Type.String({ pattern: SKILL_NAME_SLUG.source }),
}, { $id: 'SkillNameParam', additionalProperties: false });
// ─────────────────────────────────────────────────────────────────
// MCP Servers schemas
// ─────────────────────────────────────────────────────────────────
export const McpServerScanStatusSchema = Type.Union([
    Type.Literal('ok'),
    Type.Literal('no_endpoint'),
    Type.Literal('invalid_config'),
    Type.Literal('scan_failed'),
], { $id: 'McpServerScanStatus' });
export const McpServerSortBySchema = Type.Union([
    Type.Literal('name'),
    Type.Literal('scan_status'),
    Type.Literal('updated_at'),
    Type.Literal('scanned_at'),
    Type.Literal('server_type'),
], { $id: 'McpServerSortBy' });
export const ListMcpServersQuerySchema = Type.Object({
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200 })),
    cursor: Type.Optional(Type.String({ minLength: 1 })),
    q: Type.Optional(Type.String()),
    sort_by: Type.Optional(Type.Ref(McpServerSortBySchema)),
    sort_dir: Type.Optional(Type.Ref(ConfigListSortDirectionSchema)),
    scan_status: Type.Optional(Type.Ref(McpServerScanStatusSchema)),
}, { $id: 'ListMcpServersQuery', additionalProperties: false });
export const McpServerRowSchema = Type.Object({
    name: Type.String(),
    server_type: Type.String(),
    url: Type.Union([Type.String(), Type.Null()]),
    raw_config: Type.String(),
    scan_status: Type.Ref(McpServerScanStatusSchema),
    scan_error: Type.Union([Type.String(), Type.Null()]),
    scanned_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    updated_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    // fn-73.1 (R2): `true` iff this descriptor's resolved endpoint
    // canonical-matches the configured AgentCore gateway URL (and there is no
    // >1-match conflict). The console tells the gateway connector apart from a
    // direct `type:url` connector (e.g. InfoTrack) via this flag.
    is_gateway: Type.Boolean(),
    // fn-73.1 (R2): whether the console may toggle this row. `false` for a
    // non-`ok` scan_status OR a gateway-match conflict (>1 descriptor matched
    // the gateway URL → ALL matching rows fail closed).
    selectable: Type.Boolean(),
    // fn-74.1 (R3): CP-derived capability — `true` ONLY for the InfoTrack-backed
    // connector (by configured name) whose resolved URL origin is in the trusted
    // `INFOTRACK_TRUSTED_ORIGINS` allowlist. The console drives the rich per-tool
    // InfoTrack panel off THIS flag (not a hard-coded name); fail-closed false for
    // every other connector and INDEPENDENT of runtime token-source wiring.
    supports_direct_introspection: Type.Boolean(),
}, { $id: 'McpServerRow', additionalProperties: false });
export const ListMcpServersResponseSchema = Type.Object({
    data: Type.Array(Type.Ref(McpServerRowSchema)),
    next_cursor: Type.Union([Type.String(), Type.Null()]),
    last_scan_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
}, { $id: 'ListMcpServersResponse', additionalProperties: false });
export const ScanMcpServersResponseSchema = Type.Object({
    scanned_at: Type.String({ format: 'date-time' }),
    discovered_count: Type.Integer({ minimum: 0 }),
    valid_count: Type.Integer({ minimum: 0 }),
    invalid_count: Type.Integer({ minimum: 0 }),
}, { $id: 'ScanMcpServersResponse', additionalProperties: false });
// ─────────────────────────────────────────────────────────────────
// Models registry schemas (fn-72 R1)
// ─────────────────────────────────────────────────────────────────
/**
 * Coarse speed/latency hint for a canonical agent model — a UX-only
 * annotation the Add-Agent dropdown may surface (e.g. a "fast" badge).
 * Optional on the wire; absent when the registry leaves it unset.
 */
export const ModelSpeedSchema = Type.Union([Type.Literal('fast'), Type.Literal('balanced'), Type.Literal('powerful')], { $id: 'ModelSpeed' });
/**
 * One canonical agent model the platform exposes for the Add-Agent
 * model dropdown (fn-72 R1).
 *
 * **`name` IS the model id sent to MA** — the value `POST /agents`
 * forwards verbatim as `{ id: model.trim() }`. There is NO
 * name→wire-id translation layer: the dropdown's selected value goes
 * straight onto the create body. The registry is READ-ONLY UX data —
 * `POST`/`PATCH /agents` still accept any `model` string verbatim.
 */
export const ModelItemSchema = Type.Object({
    /** Canonical model id forwarded verbatim to MA on `POST /agents`. */
    name: Type.String({ minLength: 1 }),
    /** Human-readable display label for the dropdown (e.g. "Sonnet 4.6"). */
    label: Type.String({ minLength: 1 }),
    /** Exactly one registry entry is the default-selected option. */
    is_default: Type.Boolean(),
    /** Optional coarse speed hint (UX badge only). */
    speed: Type.Optional(Type.Ref(ModelSpeedSchema)),
}, { $id: 'ModelItem', additionalProperties: false });
export const ModelsListResponseSchema = Type.Object({
    data: Type.Array(Type.Ref(ModelItemSchema)),
}, { $id: 'ModelsListResponse', additionalProperties: false });
// ─────────────────────────────────────────────────────────────────
// Network Profiles schemas (fn-68 R3)
// ─────────────────────────────────────────────────────────────────
export const NetworkProfileScanStatusSchema = Type.Union([Type.Literal('ok'), Type.Literal('invalid_config'), Type.Literal('scan_failed')], { $id: 'NetworkProfileScanStatus' });
export const NetworkProfileSortBySchema = Type.Union([
    Type.Literal('name'),
    Type.Literal('scan_status'),
    Type.Literal('updated_at'),
    Type.Literal('scanned_at'),
    Type.Literal('is_default'),
], { $id: 'NetworkProfileSortBy' });
export const ListNetworkProfilesQuerySchema = Type.Object({
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 200 })),
    cursor: Type.Optional(Type.String({ minLength: 1 })),
    q: Type.Optional(Type.String()),
    sort_by: Type.Optional(Type.Ref(NetworkProfileSortBySchema)),
    sort_dir: Type.Optional(Type.Ref(ConfigListSortDirectionSchema)),
    scan_status: Type.Optional(Type.Ref(NetworkProfileScanStatusSchema)),
}, { $id: 'ListNetworkProfilesQuery', additionalProperties: false });
/**
 * The NORMALIZED networking sub-object of an `ok` profile row. Reconstructed
 * from the persisted `NetworkProfile.networking` (a config-repo catalog only
 * ever persists `mode: 'limited'` as `ok`). `null` for a non-`ok` row.
 */
export const NetworkProfileNetworkingSchema = Type.Object({
    // The contract's two modes (a config-repo `ok` row only ever carries
    // `limited`, but the published type mirrors the NetworkProfile contract so
    // generated clients keep the same guarantee the control-plane relies on).
    mode: Type.Union([Type.Literal('limited'), Type.Literal('unrestricted')]),
    // Bare, lower-cased hostnames (post-canonicalization) — no scheme/port/path.
    allowed_hosts: Type.Array(Type.String({ minLength: 1, pattern: '^[a-z0-9.-]+$' })),
}, { $id: 'NetworkProfileNetworking', additionalProperties: false });
export const NetworkProfileRowSchema = Type.Object({
    name: Type.String(),
    is_default: Type.Boolean(),
    web_tools: Type.Union([Type.String(), Type.Null()]),
    package_managers: Type.Union([Type.String(), Type.Null()]),
    networking: Type.Union([Type.Ref(NetworkProfileNetworkingSchema), Type.Null()]),
    raw_config: Type.String(),
    scan_status: Type.Ref(NetworkProfileScanStatusSchema),
    scan_error: Type.Union([Type.String(), Type.Null()]),
    scanned_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    updated_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
}, { $id: 'NetworkProfileRow', additionalProperties: false });
export const ListNetworkProfilesResponseSchema = Type.Object({
    data: Type.Array(Type.Ref(NetworkProfileRowSchema)),
    next_cursor: Type.Union([Type.String(), Type.Null()]),
    last_scan_at: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
}, { $id: 'ListNetworkProfilesResponse', additionalProperties: false });
export const ScanNetworkProfilesResponseSchema = Type.Object({
    scanned_at: Type.String({ format: 'date-time' }),
    discovered_count: Type.Integer({ minimum: 0 }),
    valid_count: Type.Integer({ minimum: 0 }),
    invalid_count: Type.Integer({ minimum: 0 }),
    /** The accepted catalog's single `default: true` profile — `null` when the catalog is empty. */
    default_profile: Type.Union([Type.String(), Type.Null()]),
}, { $id: 'ScanNetworkProfilesResponse', additionalProperties: false });
// ─────────────────────────────────────────────────────────────────
// Webhook schemas
// ─────────────────────────────────────────────────────────────────
/**
 * `POST /webhooks/:source` body. The source-specific shape is opaque
 * here — the wire is HMAC-signed and the handler verifies signature
 * before parsing. We require an explicit on-behalf-of trio
 * (`target_user_id`, `target_account_id`, `target_matter_id`) so the
 * tenancy authz check has a deterministic subject.
 *
 * `agent_id` is required: the system-initiated path cannot guess
 * which agent to spawn — the upstream automation declares it.
 */
export const WebhookPayloadSchema = Type.Object({
    agent_id: Type.String({ format: 'uuid' }),
    target_user_id: Type.String({ minLength: 1 }),
    target_account_id: Type.String({ minLength: 1 }),
    target_matter_id: Type.String({ minLength: 1 }),
    initial_message: Type.Optional(Type.String()),
    metadata: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
    // fn-79 (R2/R3) — the webhook (internal create path) threads the SAME
    // explicit audience input as `POST /sessions`. This schema is
    // `additionalProperties: false`, so WITHOUT this declared field a payload
    // carrying `audience` would be rejected/stripped at validation before the
    // route could thread it. Absent ⇒ `'user'`. NEVER derived from the
    // webhook entry point — the initiating configuration states it.
    audience: Type.Optional(Type.Ref(SessionAudienceSchema, {
        description: "Intended audience of the created session ('user' | 'internal'). Omitted ⇒ 'user'. " +
            'Never derived from the entry point.',
    })),
}, { $id: 'WebhookPayload', additionalProperties: false });
export const WebhookResponseSchema = Type.Object({
    session_id: Type.String({ format: 'uuid' }),
    status: Type.Ref(SessionStatusSchema),
}, { $id: 'WebhookResponse' });
// ─────────────────────────────────────────────────────────────────
// Files API schemas (fn-1.25)
// ─────────────────────────────────────────────────────────────────
/**
 * Stable scope union for the Files API. Mirrors `FileScope` in
 * `@sb-ai-platform/files` and the `scope` column in
 * `packages/migrations/src/schema/files.ts`. Kept here (not imported)
 * because schemas owns wire shapes — the boundary lint forbids
 * importing the impl package.
 *
 * `session` is the default applied when the upload caller doesn't
 * pass `?scope=matter`. There is **no PATCH for scope upgrade at v1**
 * (spec § API Contracts line 355 + fn-1.25 clarification) — scope is
 * set at upload time only.
 */
export const FileScopeSchema = Type.Union([Type.Literal('session'), Type.Literal('matter'), Type.Literal('account')], {
    $id: 'FileScope',
    description: '`session` — file lives and dies with the parent session. `matter` — survives session terminals, scoped to the parent matter. `account` — workspace-scoped, no parent session at upload time; survives until explicit archive (fn-2, 2026-05-17).',
});
/**
 * Wire shape of a file record returned by `POST /sessions/:id/files`
 * (201) and `GET /files/:id` (when called as a metadata probe — the
 * download path streams bytes via `application/octet-stream` instead).
 *
 * Snake_case at the HTTP boundary, mirrors the `files` Drizzle columns.
 */
export const FileSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    /** Nullable as of fn-2 for `scope='account'` rows. */
    session_id: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
    matter_id: Type.Union([Type.String(), Type.Null()]),
    /** Non-null when `scope='account'` (fn-2). */
    account_id: Type.Union([Type.String(), Type.Null()]),
    scope: Type.Ref(FileScopeSchema),
    s3_bucket: Type.String(),
    s3_key: Type.String(),
    size_bytes: Type.Integer({ minimum: 0 }),
    content_type: Type.String(),
    uploaded_by_user_id: Type.String(),
    uploaded_at: Type.String({ format: 'date-time' }),
    state: Type.Union([
        Type.Literal('pending'),
        Type.Literal('ready'),
        Type.Literal('failed'),
        Type.Literal('archived'),
    ]),
    metadata: Type.Union([Type.Record(Type.String(), Type.Unknown()), Type.Null()]),
}, { $id: 'File' });
/**
 * `POST /sessions/:id/files` query — `?scope=session|matter`. Default
 * `session` is applied at the route layer (the schema accepts the
 * value as optional). Unknown values surface as a tagged 400 via the
 * `parseScopeQuery` helper in `@sb-ai-platform/files`.
 */
/**
 * Session-upload scope is narrower than the full {@link FileScopeSchema}:
 * `POST /sessions/:id/files` only supports `session` and `matter`. `account`
 * uploads have no parent session and go through the workspace files route, so
 * accepting it here would advertise an unsupported value to OpenAPI/SDK
 * clients (it 400s server-side as `invalid_scope`).
 */
export const SessionUploadFileScopeSchema = Type.Union([Type.Literal('session'), Type.Literal('matter')], { $id: 'SessionUploadFileScope' });
export const UploadFileQuerySchema = Type.Object({
    scope: Type.Optional(Type.Ref(SessionUploadFileScopeSchema)),
}, { $id: 'UploadFileQuery', additionalProperties: false });
// Gateway tools (fn-62.2, ported + re-pointed at the live reader in fn-63.5) —
// the wire shape for `GET /admin/gateway/tools`.
//
// The CP route maps the live reader's CP-internal `GatewayToolsResult`
// (owned in `packages/control-plane/src/agentcore/gateway-tools-live.ts`)
// onto these HTTP/TypeBox types (`schemas` owns HTTP types; the reverse
// import is forbidden). It is the gateway's federated, live tool catalog
// (read via an `allowAll` introspection `tools/list`) — see the fn-63 spec.
//
// **No raw detail crosses this boundary (R5/R6):** the per-target error
// member carries only a BOUNDED `code` (+ the target metadata); gateway /
// AWS / SigV4 detail is logged server-side and never serialized.
// ─────────────────────────────────────────────────────────────────
/** Bounded, client-safe per-target error codes (mirrors the domain union). */
export const GatewayToolErrorCodeSchema = Type.Union([Type.Literal('THROTTLED'), Type.Literal('ACCESS_DENIED'), Type.Literal('UNAVAILABLE')], { $id: 'GatewayToolErrorCode' });
/** Where a target's tool catalog came from (registered inline vs unavailable). */
export const GatewayToolSourceSchema = Type.Union([Type.Literal('registered'), Type.Literal('unavailable')], { $id: 'GatewayToolSource' });
/** Best-effort read/write hint; `unknown` when the registered schema carries none. */
export const GatewayToolReadWriteSchema = Type.Union([Type.Literal('read'), Type.Literal('write'), Type.Literal('unknown')], { $id: 'GatewayToolReadWrite' });
/** A single registered tool projected from a target's `ToolDefinition`. */
export const GatewayToolSchema = Type.Object({
    id: Type.String({ description: 'Namespaced id `<target>___<tool>` (fn-57 scope ids).' }),
    name: Type.String({ description: 'The bare MCP tool name (e.g. `list_matter_types`).' }),
    description: Type.Union([Type.String(), Type.Null()]),
    input_schema: Type.Union([Type.Record(Type.String(), Type.Unknown()), Type.Null()]),
    read_write: Type.Ref(GatewayToolReadWriteSchema),
}, { $id: 'GatewayTool', additionalProperties: false });
/** Target metadata — carried even when its detail call fails or times out. */
export const GatewayTargetMetaSchema = Type.Object({
    id: Type.String(),
    name: Type.String(),
    type: Type.Union([Type.String(), Type.Null()], {
        description: 'The AgentCore `TargetType` (e.g. `LAMBDA`, `MCP_SERVER`), or null.',
    }),
    status: Type.Union([Type.String(), Type.Null()], {
        description: 'The AgentCore `TargetStatus` (e.g. `READY`, `FAILED`), or null.',
    }),
}, { $id: 'GatewayTargetMeta', additionalProperties: false });
/**
 * Per-target outcome — a discriminated union on `ok`. A single target's
 * failure (or hang) rides here as `ok:false` inside the 200 body; it never
 * fails the whole response (degraded view).
 */
export const GatewayTargetResultSchema = Type.Union([
    Type.Object({
        ok: Type.Literal(true),
        target: Type.Ref(GatewayTargetMetaSchema),
        tools: Type.Array(Type.Ref(GatewayToolSchema)),
        tool_source: Type.Ref(GatewayToolSourceSchema),
    }, { additionalProperties: false }),
    Type.Object({
        ok: Type.Literal(false),
        target: Type.Ref(GatewayTargetMetaSchema),
        error: Type.Object({ code: Type.Ref(GatewayToolErrorCodeSchema) }, { additionalProperties: false }),
    }, { additionalProperties: false }),
], { $id: 'GatewayTargetResult' });
/**
 * The `GET /admin/gateway/tools` 200 body.
 *   - `gateway_configured:false` ⇒ this stage has no gateway (a NORMAL
 *     state, not an error). `targets` is empty, `partial` is false.
 *   - `partial:true` ⇒ at least one target is `ok:false` (degraded view).
 */
export const GatewayToolsResponseSchema = Type.Object({
    gateway_configured: Type.Boolean({
        description: 'False ⇒ no gateway configured for this stage (normal, not an error).',
    }),
    gateway_url: Type.Union([Type.String(), Type.Null()], {
        description: 'The gateway endpoint these tools were introspected from, or null when no gateway is configured. Lets a client attribute the tools to the declared MCP server whose URL matches — no provider-specific logic.',
    }),
    targets: Type.Array(Type.Ref(GatewayTargetResultSchema)),
    partial: Type.Boolean({
        description: 'True ⇒ at least one target failed (its error rides in `targets`).',
    }),
}, { $id: 'GatewayToolsResponse', additionalProperties: false });
// ─────────────────────────────────────────────────────────────────
// Per-session tool-config override (fn-69 — Shape 1: HTTP caller DELTA).
//
// The minimal narrowing a caller expresses for a session, relative to the
// agent baseline. It is a DELTA, NOT a full config: omitted servers / tools /
// built-ins keep their baseline. The control-plane resolver (fn-69 task .3)
// composes this onto the pinned `config_snapshot` to produce the full
// `ResolvedSessionAgentConfig` (in `@sb-ai-platform/platform-contracts`).
//
// **Narrowing-only (ADR 0026).** A caller may ONLY narrow — disable a tool /
// connector, escalate a tool's permission to `always_ask`, or disable a
// native built-in. It can NEVER widen beyond the agent-declared set or
// de-escalate below baseline. That is why the per-tool permission enum is
// `baseline | always_ask | disabled` and there is deliberately NO
// `always_allow`: an `always_allow` value would (a) overload "leave at
// baseline" with a policy and (b) let a client DE-escalate an agent's
// `always_ask` tool. "Allowed" is a UI label shown only when the baseline
// permits — never a caller-settable value here.
//
// **Native built-ins (R14).** Toggled via `native_builtins` — a
// `Record<declared-built-in-name, boolean>` (declared name → enabled), NOT
// fixed `web_search`/`web_fetch` fields, so ANY `agent_toolset_20260401`
// built-in the agent declares can be toggled. The resolver validates each
// named built-in against the pinned baseline. These are MA-native (sandbox)
// tools that never traverse the gateway — a distinct concept from
// gateway-injected `allowedBuiltins`.
//
// The capped, enum-validating NORMALIZER lives in `./session-tool-config.ts`
// and is re-exported below. It caps BEFORE trim/normalize against a shared cap
// constant (route cap == resolver cap), enforces the byte cap on the RAW
// request bytes when the route passes `options.rawByteLength` (whitespace a
// later parse discards still counts), and rejects prototype-pollution keys
// (`__proto__` / `prototype` / `constructor`) while returning null-prototype
// maps.
// ─────────────────────────────────────────────────────────────────
/**
 * Per-tool HITL permission a caller may request. Narrowing-only:
 *   - `baseline` — leave the tool at the agent's declared permission (the
 *     no-op default; the resolver reads the true baseline).
 *   - `always_ask` — ESCALATE to needs-approval (HITL). Always allowed.
 *   - `disabled` — drop the tool entirely (removed from the resolved scope;
 *     the gateway then denies the `tools/call`).
 *
 * NO `always_allow` — see the section header for why a caller can never
 * de-escalate.
 */
export const SessionToolPermissionSchema = Type.Union([Type.Literal('baseline'), Type.Literal('always_ask'), Type.Literal('disabled')], { $id: 'SessionToolPermission' });
/**
 * One configurable MCP server's caller-requested override. `disabled`
 * disables the WHOLE connector (all its tools); `tools` carries per-tool
 * permission overrides keyed by the bare or qualified tool name (the
 * resolver qualifies + validates against the pinned snapshot + live
 * `GatewayTool` catalog). Both fields optional — an empty object is a no-op
 * for that server.
 */
export const SessionToolConfigServerOverrideSchema = Type.Object({
    /** Disable the entire connector for this session. */
    disabled: Type.Optional(Type.Boolean()),
    /** Per-tool permission overrides, keyed by tool name. */
    tools: Type.Optional(Type.Record(Type.String(), Type.Ref(SessionToolPermissionSchema))),
}, { $id: 'SessionToolConfigServerOverride', additionalProperties: false });
/**
 * The `tool_config` body field (caller DELTA). Per logical
 * `mcp_server_name`: a {@link SessionToolConfigServerOverride}; plus
 * `native_builtins` (declared-built-in-name → enabled). All fields optional —
 * an empty override is a valid no-op. Sent on `POST /sessions` (create-time)
 * and `POST /sessions/{id}/tool-config` (update). The resolver enforces
 * narrowing-only + denylist + caps; this schema only pins the shape + enums.
 */
export const SessionToolConfigOverrideSchema = Type.Object({
    /** Per-`mcp_server_name` override. */
    servers: Type.Optional(Type.Record(Type.String(), Type.Ref(SessionToolConfigServerOverrideSchema))),
    /** Declared native-built-in toggles (declared name → enabled). */
    native_builtins: Type.Optional(Type.Record(Type.String(), Type.Boolean())),
}, { $id: 'SessionToolConfigOverride', additionalProperties: false });
/**
 * Body of `POST /sessions/{id}/tool-config` (the in-session update). Carries
 * the COMPLETE desired override (a full desired-override replacement — omitted
 * entries revert to baseline; idempotent), under the locked `tool_config`
 * field (same name as create-time). The route caps + normalizes it.
 */
export const PostSessionToolConfigBodySchema = Type.Object({
    tool_config: Type.Ref(SessionToolConfigOverrideSchema),
}, { $id: 'PostSessionToolConfigBody', additionalProperties: false });
// ─────────────────────────────────────────────────────────────────
// fn-69 (R10) — CP non-admin config-list projection (the configurable-
// tools view the pre-start panel + in-session dropdown render). Sourced
// from the AGENT's declared tools (not the admin gateway catalog), enriched
// server-side from the live `GatewayTool` catalog. Two display degradations:
// per-tool `read_write:"unknown"` (identities known, hint missing) and a
// connector-level `catalog_unavailable` (whole-server allowAll + catalog
// down — no per-tool list).
// ─────────────────────────────────────────────────────────────────
/** Catalog read/write hint, degrading to `unknown` when the hint can't be fetched. */
export const ConfigurableToolReadWriteSchema = Type.Union([Type.Literal('read'), Type.Literal('write'), Type.Literal('unknown')], { $id: 'ConfigurableToolReadWrite' });
/** Baseline (agent-declared, post-denylist) HITL permission of a configurable MCP tool. */
export const ConfigurableToolBaselinePermissionSchema = Type.Union([Type.Literal('always_allow'), Type.Literal('always_ask'), Type.Literal('always_deny')], { $id: 'ConfigurableToolBaselinePermission' });
/** One configurable MCP tool projected for the UI. */
export const ConfigurableToolSchema = Type.Object({
    /** Logical `mcp_server_name` the tool belongs to. */
    server_id: Type.String(),
    /** Qualified federated `<target>___<tool>` id. */
    tool_id: Type.String(),
    /** Bare tool name (suffix). */
    tool_name: Type.String(),
    /**
     * fn-72 (R3/R4) — the federated TARGET prefix this tool came from (the
     * `<target>` of `<target>___<tool>`; the gateway-builtin pseudo-target for a
     * non-namespaced tool). DISTINCT from {@link server_id} (the logical
     * connector): one connector (`smokeball`) surfaces tools across MANY targets.
     * The UI groups by this, never re-deriving it from the `tool_id` prefix.
     */
    target_id: Type.String(),
    /**
     * fn-72 (R4) — a human-friendly label for {@link target_id} (e.g. `Smokeball`,
     * `Forms`, `Matter types`, `Archie`; Title-Case fallback for an unknown
     * target). Display-only — never an authorization signal.
     */
    area_label: Type.String(),
    /** Friendly display name (catalog-sourced; falls back to `tool_name`). */
    display_name: Type.String(),
    /** Catalog read/write hint (or `unknown` when the catalog is degraded). */
    read_write: Type.Ref(ConfigurableToolReadWriteSchema),
    /** The agent-declared (post-denylist) baseline permission. */
    baseline_permission: Type.Ref(ConfigurableToolBaselinePermissionSchema),
    /** `true` when on the session-scope denylist (forced `always_ask`). */
    denylist_locked: Type.Boolean(),
    /** `true` when the agent declares the tool `always_deny` (locked disabled). */
    always_deny_locked: Type.Boolean(),
    /**
     * fn-74 (R5, HITL lock) — `true` when the tool's permission is LOCKED at
     * `always_ask` and the UI must NOT offer `always_allow`. TRUE for every
     * DIRECT-connector (`supports_direct_introspection`) tool in fn-74: direct
     * tools are reached outside the AgentCore gateway, so the gateway scope
     * resolver cannot gate them — MA HITL (`always_ask`) is the only control,
     * and a client-supplied `readOnlyHint` cannot be trusted at persist without
     * resolver-side re-introspection (deferred). OPTIONAL with a `false` default
     * so existing gateway-tool rows + the SDK stay backward-compatible (a row
     * without the flag is unlocked). The UI renders a locked tool's permission
     * as a fixed "Always ask".
     */
    always_allow_locked: Type.Optional(Type.Boolean()),
    /**
     * SESSION variant only — the currently-APPLIED override for this tool
     * (omitted on the agent baseline endpoint, and on the session endpoint
     * when no override is applied to this tool). `disabled` ⇒ dropped from
     * scope; `always_ask` ⇒ escalated; `baseline` ⇒ left at baseline.
     */
    applied_permission: Type.Optional(Type.Ref(SessionToolPermissionSchema)),
}, { $id: 'ConfigurableTool', additionalProperties: false });
/** One configurable connector (a declared `mcp_toolset`) + its tools. */
export const ConfigurableConnectorSchema = Type.Object({
    server_id: Type.String(),
    /**
     * Connector-level DISPLAY degradation (R10): `true` for a whole-server
     * `allowAll` connector whose per-tool rows can't be enumerated because the
     * catalog is down. The UI then shows a single enable/disable, NOT a
     * misleading empty per-tool list. `tools` is empty in that case.
     */
    catalog_unavailable: Type.Boolean(),
    /**
     * fn-74 (R4) — DIRECT-connector failure isolation: `true` when a
     * direct-introspectable connector (e.g. InfoTrack) has NO firm token
     * (`no_token`), so the per-tool catalog could not be read AND the admin
     * must authenticate to InfoTrack before per-tool config is available. The
     * UI shows "Log in to InfoTrack first" alongside the still-available
     * whole-server enable (fn-73 fallback). DISTINCT from `catalog_unavailable`
     * (a transient `unavailable`/timeout/error sets only `catalog_unavailable`,
     * NO `login_required`). NEVER set on the gateway connector. OPTIONAL with a
     * `false` default (backward-compatible).
     */
    login_required: Type.Optional(Type.Boolean()),
    /** SESSION variant only — `true` when the connector is disabled by the applied override. */
    applied_disabled: Type.Optional(Type.Boolean()),
    tools: Type.Array(Type.Ref(ConfigurableToolSchema)),
}, { $id: 'ConfigurableConnector', additionalProperties: false });
/** One configurable native built-in (MA-native; never gateway-traversing). */
export const ConfigurableNativeBuiltinSchema = Type.Object({
    name: Type.String(),
    /** The agent-declared baseline enabled state. */
    baseline_enabled: Type.Boolean(),
    /** SESSION variant only — the applied override's enabled state (when overridden). */
    applied_enabled: Type.Optional(Type.Boolean()),
}, { $id: 'ConfigurableNativeBuiltin', additionalProperties: false });
// Shared between the two endpoint-specific response schemas below. The agent
// baseline endpoint has NO applied/degraded state; the session endpoint always
// carries `degraded` (so it is REQUIRED there — a caller must not have to treat
// the send-blocking flag as possibly-absent).
const CONFIGURABLE_TOOLS_BASE_PROPS = {
    connectors: Type.Array(Type.Ref(ConfigurableConnectorSchema)),
    native_builtins: Type.Array(Type.Ref(ConfigurableNativeBuiltinSchema)),
    /** `false` when the live catalog could not be fully read (display degraded). */
    catalog_available: Type.Boolean(),
};
/**
 * Response of `GET /agents/{id}/configurable-tools` (agent baseline) +
 * `GET /sessions/{id}/configurable-tools` (in-session). The session variant
 * additionally REQUIRES `degraded`; this union type keeps `degraded` optional so
 * the shared projector can return either shape. Route schemas use the two
 * endpoint-specific variants below (so OpenAPI/SDK reflect the right
 * requiredness per endpoint).
 */
export const ConfigurableToolsResponseSchema = Type.Object({
    ...CONFIGURABLE_TOOLS_BASE_PROPS,
    /**
     * SESSION variant only — `true` when the session's enforced scope diverges
     * from the applied override intent (after a failed/ambiguous update or a
     * crash+TTL release). Never present on the agent baseline endpoint.
     */
    degraded: Type.Optional(Type.Boolean()),
}, { $id: 'ConfigurableToolsResponse', additionalProperties: false });
/**
 * Agent-baseline `GET /agents/{id}/configurable-tools` response — NO
 * applied/degraded state (the agent has no session-scoped override). `degraded`
 * is absent by construction.
 */
export const AgentConfigurableToolsResponseSchema = Type.Object({ ...CONFIGURABLE_TOOLS_BASE_PROPS }, { $id: 'AgentConfigurableToolsResponse', additionalProperties: false });
/**
 * Session `GET /sessions/{id}/configurable-tools` response — `degraded` is
 * REQUIRED (always present for a session; it is the send-blocking flag a caller
 * must always be able to read, so the SDK must not type it `boolean | undefined`
 * — CodeRabbit).
 */
export const SessionConfigurableToolsResponseSchema = Type.Object({
    ...CONFIGURABLE_TOOLS_BASE_PROPS,
    /**
     * `true` when the session's enforced scope diverges from the applied
     * override intent (failed/ambiguous update or crash+TTL release). Always
     * present on this endpoint.
     */
    degraded: Type.Boolean(),
}, { $id: 'SessionConfigurableToolsResponse', additionalProperties: false });
/** Response of `POST /sessions/{id}/tool-config` (the in-session update). */
export const PostSessionToolConfigResponseSchema = Type.Object({
    accepted: Type.Boolean(),
    session_id: Type.String({ format: 'uuid' }),
    status: Type.Ref(SessionStatusSchema),
    /** `true` if the applied override could not be confirmed live (degraded). */
    degraded: Type.Boolean(),
}, { $id: 'PostSessionToolConfigResponse', additionalProperties: false });
// The capped, enum-validating normalizer for `SessionToolConfigOverride`.
// Behaviour + the SHARED cap constant live in `./session-tool-config.ts`
// (so the route cap == the resolver cap — fn-69 R8). Re-exported here as the
// single public entry point alongside the schema above.
export { SESSION_TOOL_CONFIG_CAPS, SessionToolConfigValidationError, normalizeSessionToolConfigOverride, } from './session-tool-config.js';
// The scope-enum-validating normalizer for the `POST /sessions` top-level
// `memory` field. Behaviour lives in `./session-memory.ts` (schemas owns
// request normalizers — fn-3): unknown/duplicate scopes map to the exact
// `400 memory_config_invalid` at the route, never a generic Ajv 400, and
// `[]` normalizes to absent (a no-op).
export { SESSION_MEMORY_SCOPES, SessionMemoryValidationError, isSessionMemoryScope, normalizeSessionMemoryScopes, } from './session-memory.js';
// fn-80 — the agent-config `memory:` ACCESS-MAP parser (scope → access mode;
// distinct from the session `memory` REQUEST field above). The single shared
// runtime definition consumed by BOTH fail-closed layers: the config-repo
// scanner's deploy-time validation and the session-create pinned-map
// backstop. Behaviour lives in `./agent-memory-access.ts`.
export { MEMORY_ACCESS_MODES, MEMORY_ACCESS_SCOPES, isMemoryAccessMode, isMemoryAccessScope, parseMemoryAccessMap, } from './agent-memory-access.js';
// ─────────────────────────────────────────────────────────────────
// Config-list query normalizers (the single canonical normalization
// layer for the three list query shapes — consumed by BFF, CP routes,
// and the cursor engine). Behaviour lives in `./config-list-query.ts`;
// it derives its allowed enum values from the union schemas above.
// ─────────────────────────────────────────────────────────────────
export { CONFIG_LIST_QUERY_VALIDATION_ORDER, ConfigListQueryValidationError, normalizeListConfigRepoAgentsQuery, normalizeListConfigRepoSkillsQuery, normalizeListMcpServersQuery, normalizeListNetworkProfilesQuery, } from './config-list-query.js';
// Legacy `__packageName` export kept for the scaffold-era smoke tests.
export const __packageName = '@sb-ai-platform/schemas';
//# sourceMappingURL=index.js.map